/**
 * Calendar event aggregation. Pulls tasks, quote follow-ups, sales meetings and
 * reorder windows into one dated CalEvent model the month calendar renders and
 * pushes to Google. Fuzzy due labels ("Today", "Fri 2:00 PM", "This week",
 * "Overdue", "Next quarter") are resolved to concrete Dates so they land on a
 * real calendar grid and sync cleanly.
 */
import type { WorkTask, CalEvent as WfEvent } from '../WorkflowContext';
import type { FollowUp, ReorderCandidate } from './salesData';

export type CalType = 'task' | 'followup' | 'meeting' | 'reorder';
export interface CalEvent {
  id: string;
  date: Date;
  allDay: boolean;
  time?: string;
  title: string;
  type: CalType;
  client?: string;
  clientId?: string;
  detail?: string;
  source: 'crm' | 'google';
  link?: string;
}

export const TYPE_META: Record<CalType, { label: string; color: string }> = {
  task: { label: 'Tasks & deadlines', color: 'var(--neon-blue)' },
  followup: { label: 'Quote follow-ups', color: 'var(--yl)' },
  meeting: { label: 'Sales meetings', color: 'var(--ac)' },
  reorder: { label: 'Reorder windows', color: 'var(--gn)' },
};

const WD = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

function atTime(d: Date, hh: number, mm: number): Date { const x = new Date(d); x.setHours(hh, mm, 0, 0); return x; }
function startOfDay(d: Date): Date { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }

/** Resolve a fuzzy due string to a concrete date (+ optional time). */
export function resolveDue(due: string, base: Date = new Date()): { date: Date; time?: string; allDay: boolean } {
  const raw = (due || '').trim();
  if (!raw) return { date: startOfDay(base), allDay: true };

  // explicit ISO / locale date (contains a separator and parses)
  if (/\d{4}-\d{2}-\d{2}/.test(raw) || /\d{1,2}\/\d{1,2}\/\d{2,4}/.test(raw)) {
    const t = Date.parse(raw);
    if (!Number.isNaN(t)) { const d = new Date(t); return { date: d, allDay: !/\d{1,2}:\d{2}/.test(raw) }; }
  }

  const s = raw.toLowerCase();
  // extract a clock time if present, e.g. "2:00 pm" or "9 am"
  let time: string | undefined;
  let hh = 9, mm = 0, hasTime = false;
  const tm = s.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)/);
  if (tm) {
    hasTime = true;
    hh = parseInt(tm[1], 10) % 12; mm = tm[2] ? parseInt(tm[2], 10) : 0;
    if (tm[3] === 'pm') hh += 12;
    time = `${tm[1]}${tm[2] ? ':' + tm[2] : ''} ${tm[3].toUpperCase()}`;
  }

  const mk = (d: Date) => hasTime ? { date: atTime(d, hh, mm), time, allDay: false } : { date: startOfDay(d), allDay: true };

  if (s.includes('today')) return mk(base);
  if (s.includes('tomorrow')) return mk(addDays(base, 1));
  if (s.includes('yesterday') || s.includes('overdue')) return mk(addDays(base, -1));
  if (s.includes('next week')) return mk(addDays(base, 7));
  if (s.includes('this week')) return mk(addDays(base, 3));
  if (s.includes('last week')) return mk(addDays(base, -7));
  if (s.includes('this month')) return mk(addDays(base, 14));
  if (s.includes('next quarter')) return mk(addDays(base, 60));
  if (s.includes('next month')) return mk(addDays(base, 30));

  // weekday name → next occurrence on/after today
  const wdIdx = WD.findIndex((w) => s.startsWith(w));
  if (wdIdx >= 0) {
    const cur = base.getDay();
    let delta = (wdIdx - cur + 7) % 7; // 0..6 ; today if same day
    const d = addDays(base, delta);
    return mk(d);
  }

  // fallback: today
  return mk(base);
}

function addDays(d: Date, n: number): Date { const x = new Date(d); x.setDate(x.getDate() + n); return x; }

const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40);

export function tasksToEvents(tasks: WorkTask[], base?: Date): CalEvent[] {
  return tasks.filter((t) => t.status !== 'completed').map((t) => {
    const r = resolveDue(t.due, base);
    return { id: `task_${t.id}`, date: r.date, allDay: r.allDay, time: r.time, title: t.title, type: 'task', client: t.client, detail: `${t.owner} · ${t.dept} · ${t.status.replace('_', ' ')}`, source: 'crm' as const };
  });
}

export function followUpsToEvents(items: FollowUp[], base?: Date): CalEvent[] {
  return items.filter((f) => f.kind !== 'reorder').map((f, i) => {
    const r = resolveDue(f.due, base);
    return { id: `fu_${slug(f.clientId)}_${slug(f.label)}_${i}`, date: r.date, allDay: r.allDay, time: r.time, title: f.label, type: 'followup', client: f.clientName, clientId: f.clientId, detail: f.detail, source: 'crm' as const };
  });
}

export function reordersToEvents(items: ReorderCandidate[], base?: Date): CalEvent[] {
  return items.map((c) => {
    const r = resolveDue(c.window, base);
    return { id: `re_${slug(c.clientId)}_${slug(c.sku.sku)}`, date: r.date, allDay: true, title: `Reorder window: ${c.sku.sku}`, type: 'reorder', client: c.clientName, clientId: c.clientId, detail: `${c.window} · ${c.confidence}% · last ${c.sku.lastOrder}`, source: 'crm' as const };
  });
}

export function meetingsToEvents(events: WfEvent[], base?: Date): CalEvent[] {
  return events.map((e, i) => {
    const r = resolveDue(`${e.day} ${e.time}`, base);
    return { id: `mtg_${slug(e.title)}_${i}`, date: r.date, allDay: r.allDay, time: r.time, title: e.title, type: 'meeting', detail: e.dept, source: 'crm' as const };
  });
}

export const ymdKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
